import React, { useEffect, useMemo, useState } from 'react';
import { PaymentRedirect } from '../types';
import StripePayment from './StripePayment';
import './PaymentView.css';

type PaymentMethodOption = 'card';

interface PaymentViewProps {
    paymentRedirect: PaymentRedirect | null;
    onPaymentComplete?: () => void;
}

const PaymentView: React.FC<PaymentViewProps> = ({ paymentRedirect, onPaymentComplete }) => {
    const stripeReady = Boolean(
        paymentRedirect?.stripeClientSecret &&
        paymentRedirect?.stripePublishableKey
    );

    const cardAvailable = stripeReady;

    const [selectedMethod, setSelectedMethod] = useState<PaymentMethodOption | null>(() => {
        if (cardAvailable) {
            return 'card';
        }
        return null;
    });

    useEffect(() => {
        if (selectedMethod === 'card' && !cardAvailable) {
            setSelectedMethod(null);
        }
    }, [cardAvailable, selectedMethod]);

    if (!paymentRedirect || !paymentRedirect.redirectToPayment) {
        return (
            <div className="payment-view">
                <div className="payment-view__status payment-view__status--warning" role="status">
                    This order does not require an online payment.
                </div>
            </div>
        );
    }

    if (!selectedMethod) {
        return (
            <div className="payment-view">
                <div className="payment-view__status payment-view__status--error" role="alert">
                    No payment providers are currently available. Please contact the restaurant to complete your order.
                </div>
            </div>
        );
    }

    const formattedTotal = useMemo(() => {
        if (typeof paymentRedirect.amount !== 'number') {
            return null;
        }

        const currencyCode = paymentRedirect.currency || 'GBP';

        try {
            const formatter = new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: currencyCode
            });
            return formatter.format(paymentRedirect.amount);
        } catch (error) {
            console.warn('Failed to format currency for payment summary', error);
            const symbol = currencyCode === 'GBP' ? '£' : `${currencyCode} `;
            return `${symbol}${paymentRedirect.amount.toFixed(2)}`;
        }
    }, [paymentRedirect.amount, paymentRedirect.currency]);

    return (
        <div className="payment-view">
            {formattedTotal && (
                <div className="payment-view__summary" role="status">
                    Total due: <strong>{formattedTotal}</strong>
                </div>
            )}

            <div className="payment-view__panel">
                {cardAvailable && (
                    <StripePayment
                        clientSecret={paymentRedirect.stripeClientSecret ?? null}
                        publishableKey={paymentRedirect.stripePublishableKey ?? null}
                        orderId={paymentRedirect.orderId}
                        amount={paymentRedirect.amount}
                        currency={paymentRedirect.currency}
                        isActive={selectedMethod === 'card'}
                        onPaymentComplete={onPaymentComplete}
                    />
                )}
            </div>
        </div>
    );
};

export default PaymentView;
